// src/components/ambassador/AmbassadorHome.jsx
//
// /ambassador - what to do next: visits waiting for their result, active
// reservations with their countdown, follow-ups this ambassador owns.
// Earnings, XP and missions come with lots 2-3.
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Container, Typography, Paper, Stack, Button, Box, Chip } from '@mui/material';
import { useAmbassador } from './AmbassadorApp';
import useNow from './useNow';
import { formatRemaining } from '../../utils/ambassadorStatus';
import { ambassadorGet } from '../../utils/ambassadorApi';

const Card = ({ to, title, subtitle, right, highlight }) => (
  <Paper component={RouterLink} to={to} variant="outlined"
    sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, textDecoration: 'none', color: 'inherit',
      borderColor: highlight ? '#0b4dff' : undefined, borderWidth: highlight ? 2 : 1 }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600 }} noWrap>{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary" noWrap>{subtitle}</Typography>}
    </Box>
    {right}
  </Paper>
);

export default function AmbassadorHome() {
  const { me, reloadMe, logout, getToken } = useAmbassador();
  const now = useNow();
  useEffect(() => { reloadMe(); }, [reloadMe]);

  // The kiosks' demo restaurants: their statistics are open to ambassadors.
  const [demo, setDemo] = useState([]);
  useEffect(() => {
    ambassadorGet('/demo-facilities', getToken()).then((r) => setDemo(r.facilities || [])).catch(() => setDemo([]));
  }, [getToken]);

  const { reservations, followUps, openVisits, rules } = me;
  const date = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="text.secondary">QRProofs Ambassadeur</Typography>
        <Button size="small" onClick={logout}>Déconnexion</Button>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Bonjour {me.ambassador.firstName}</Typography>

      <Button component={RouterLink} to="/ambassador/map" variant="contained" size="large" fullWidth sx={{ py: 1.5, mb: 3, fontWeight: 700 }}>
        Trouver un établissement
      </Button>

      {openVisits.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Résultat à donner</Typography>
          <Stack spacing={1}>
            {openVisits.map((v) => (
              <Card key={v.visitId} to={`/ambassador/prospects/${v.prospectId}`} highlight title={v.name}
                subtitle={`Visite à ${new Date(v.checkinAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`}
                right={<Chip size="small" color="primary" label="Compléter" />} />
            ))}
          </Stack>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Mes réservations ({reservations.length}/{rules.maxActiveReservations})
        </Typography>
        {reservations.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Aucune. Réservez un établissement sur la carte avant d’y aller : personne d’autre ne pourra le démarcher pendant {rules.reservationMinutes} min.
          </Typography>
        )}
        <Stack spacing={1}>
          {reservations.map((r) => (
            <Card key={r.id} to={`/ambassador/prospects/${r.id}`} title={r.name} subtitle={r.address}
              right={<Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatRemaining(r.reservedUntil, now)}</Typography>} />
          ))}
        </Stack>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Mes relances</Typography>
        {followUps.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Aucune. Un établissement « à rappeler » ou « intéressé » vous est réservé {rules.followUpDays} jours.
          </Typography>
        )}
        <Stack spacing={1}>
          {followUps.map((f) => (
            <Card key={f.id} to={`/ambassador/prospects/${f.id}`} title={f.name} subtitle={f.address}
              right={<Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>jusqu’au {date(f.followUpUntil)}</Typography>} />
          ))}
        </Stack>
      </Box>

      {demo.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Démo</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Une borne pas encore associée propose ces restaurants fictifs. Faites noter le prospect, puis montrez-lui
            les statistiques : note, tendance et comparaison avec la moyenne du quartier.
          </Typography>
          <Stack spacing={1}>
            {demo.map((f) => (
              <Card key={f.id} to={`/Statistic/${f.id}?lng=fr`} title={f.name}
                subtitle={f.ratings ? `★ ${f.note.toFixed(1).replace('.', ',')} · ${f.ratings} avis` : 'Pas encore d’avis'}
                right={<Chip size="small" label="Statistiques" />} />
            ))}
          </Stack>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 1.5, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Installer une borne</Typography>
        <Typography variant="body2" color="text.secondary">
          1. Branchez-la : elle affiche « Configuration ».<br />
          2. App <b>ESP BLE Provisioning</b> : scannez son QR, choisissez le Wi-Fi du restaurant.<br />
          3. Elle affiche « Associer cette borne » : scannez ce QR avec l’appareil photo et choisissez l’établissement.
        </Typography>
      </Paper>

      {me.territories.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          Vos territoires : {me.territories.map((t) => t.name).join(', ')}
        </Typography>
      )}
    </Container>
  );
}
